import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Check, CreditCard, Lightning, Sparkle, Star, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { fmtDate } from "@/lib/score-utils";

const PLAN_CARDS = [
  {
    key: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    desc: "Sanity check small sites without paying a cent.",
    bullets: ["3 audits / month", "Score overview only", "No PDF / JSON export", "No AI recommendations"],
    cta: null,
    accent: false,
  },
  {
    key: "pro",
    name: "Pro",
    price: "$19",
    period: "/ 30 days",
    desc: "For freelancers and consultants running regular client audits.",
    bullets: ["100 audits / month", "PDF + JSON export", "Prioritised recommendations", "Audit history & comparisons"],
    cta: "pro_monthly",
    accent: true,
  },
  {
    key: "agency",
    name: "Agency",
    price: "$49",
    period: "/ 30 days",
    desc: "Power-users running audits at scale across many sites.",
    bullets: ["Unlimited audits", "Everything in Pro", "Priority queue", "Coming: scheduled re-audits"],
    cta: "agency_monthly",
    accent: false,
  },
];

const CREDIT_PACKS = [
  { id: "credits_10",  count: 10,  price: "$5",  saving: "" },
  { id: "credits_50",  count: 50,  price: "$20", saving: "Save 20%" },
  { id: "credits_200", count: 200, price: "$60", saving: "Save 40%" },
];

export default function BillingPage() {
  const { billing, refreshBilling } = useAuth();
  const [buying, setBuying] = useState(null);
  const loc = useLocation();
  const nav = useNavigate();

  // Handle return from Stripe (session_id query param)
  useEffect(() => {
    const qs = new URLSearchParams(loc.search);
    const sessionId = qs.get("session_id");
    const canceled = qs.get("canceled");
    if (canceled) {
      toast.message("Payment canceled");
      nav("/app/billing", { replace: true });
      return;
    }
    if (!sessionId) return;
    toast.message("Confirming your payment…");
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" && data.applied) {
          toast.success("Payment successful — your plan is active");
          await refreshBilling();
          nav("/app/billing", { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired");
          nav("/app/billing", { replace: true });
          return;
        }
        if (attempts < 8) setTimeout(tick, 2500);
        else {
          toast.error("Status check timed out — your account will update once Stripe confirms.");
          nav("/app/billing", { replace: true });
        }
      } catch {
        toast.error("Could not verify payment");
        nav("/app/billing", { replace: true });
      }
    };
    tick();
  }, [loc.search, nav, refreshBilling]);

  const buy = async (packageId) => {
    setBuying(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start checkout");
      setBuying(null);
    }
  };

  if (!billing) {
    return (
      <AppShell>
        <div className="text-ink-muted">Loading…</div>
      </AppShell>
    );
  }

  const plan = billing.plan;
  const usage = billing.usage;
  const pct = usage.monthly_cap > 0 ? Math.min(100, (usage.audits_this_period / usage.monthly_cap) * 100) : 0;

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-6 mb-10">
        <div>
          <div className="label-overline mb-2">Billing</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none">
            Plan & usage
          </h1>
        </div>
      </div>

      {/* Current plan + usage */}
      <div className="grid lg:grid-cols-3 gap-4 mb-12">
        <div className="surface-card p-6" data-testid="current-plan-card">
          <div className="label-overline mb-2">Current plan</div>
          <div className="font-display text-3xl font-black tracking-tight mb-2">{billing.plan_label}</div>
          {plan === "trial" && billing.trial_ends_at && (
            <div className="text-xs text-ink-muted mono">Trial ends {fmtDate(billing.trial_ends_at)}</div>
          )}
          {(plan === "pro" || plan === "agency") && billing.plan_expires_at && (
            <div className="text-xs text-ink-muted mono">Renews / expires {fmtDate(billing.plan_expires_at)}</div>
          )}
          {plan === "free" && (
            <div className="text-xs text-ink-muted">Upgrade for AI summaries, exports, and more audits.</div>
          )}
        </div>

        <div className="surface-card p-6" data-testid="usage-card">
          <div className="label-overline mb-2">Usage this period</div>
          <div className="num-display text-4xl mb-2">
            {usage.audits_this_period}
            <span className="text-ink-muted text-2xl">
              {" / "}{usage.monthly_cap === -1 ? "∞" : usage.monthly_cap}
            </span>
          </div>
          {usage.monthly_cap > 0 && (
            <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
              <div className={`h-full ${pct >= 90 ? "bg-critical" : pct >= 60 ? "bg-warning" : "bg-success"}`}
                style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>

        <div className="surface-card p-6" data-testid="credits-card">
          <div className="label-overline mb-2">Audit credits</div>
          <div className="num-display text-4xl mb-2">{billing.credits}</div>
          <div className="text-xs text-ink-muted">Used when your monthly plan quota runs out.</div>
        </div>
      </div>

      {/* Subscription plans */}
      <div className="mb-12">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-overline mb-1">Subscription</div>
            <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight">Choose a 30-day pass</h2>
          </div>
          <span className="text-xs text-ink-muted mono">Renew anytime · no auto-charge</span>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {PLAN_CARDS.map((p) => {
            const isCurrent = billing.plan === p.key || (billing.plan === "trial" && p.key === "pro");
            return (
              <div
                key={p.key}
                data-testid={`plan-card-${p.key}`}
                className={`relative p-6 rounded-sm border ${
                  p.accent ? "border-ink hard-shadow bg-white" : "border-border bg-white"
                } flex flex-col`}
              >
                {p.accent && (
                  <span className="absolute -top-3 left-6 mono text-[10px] tracking-widest font-bold bg-brand text-white px-2 py-1 rounded-sm">
                    MOST POPULAR
                  </span>
                )}
                <div className="label-overline mb-2">{p.name}</div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="num-display text-5xl">{p.price}</span>
                  <span className="text-sm text-ink-muted">{p.period}</span>
                </div>
                <p className="text-sm text-ink-muted mb-5">{p.desc}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-success mt-0.5 shrink-0" weight="bold" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {p.cta ? (
                  <button
                    onClick={() => buy(p.cta)}
                    disabled={buying === p.cta || isCurrent}
                    data-testid={`buy-plan-${p.key}`}
                    className={`w-full py-3 rounded-sm font-semibold transition-all ${
                      p.accent
                        ? "bg-ink text-white hover:bg-brand hover:-translate-y-0.5"
                        : "border border-ink text-ink hover:bg-ink hover:text-white"
                    } disabled:opacity-50 disabled:hover:translate-y-0`}
                  >
                    {isCurrent ? "Active" : buying === p.cta ? "Redirecting…" : `Get ${p.name}`}
                  </button>
                ) : (
                  <div className="text-center text-xs text-ink-muted mono py-3">
                    {isCurrent ? "Your current plan" : "Default tier"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Credit packs */}
      <div className="mb-12">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-overline mb-1">Pay-as-you-go</div>
            <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight">Top up audit credits</h2>
          </div>
          <span className="text-xs text-ink-muted mono">Credits never expire</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {CREDIT_PACKS.map((c) => (
            <div key={c.id} data-testid={`credit-pack-${c.id}`}
              className="surface-card p-6 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <Sparkle size={20} className="text-brand" weight="duotone" />
                {c.saving && <span className="mono text-[10px] tracking-wider px-2 py-0.5 border border-success text-success rounded-sm">{c.saving}</span>}
              </div>
              <div className="num-display text-4xl mb-1">{c.count}</div>
              <div className="text-sm text-ink-muted mb-4">audit credits</div>
              <div className="flex items-baseline justify-between mb-5">
                <span className="num-display text-2xl">{c.price}</span>
                <span className="text-xs text-ink-muted mono">${(parseFloat(c.price.slice(1)) / c.count).toFixed(2)}/audit</span>
              </div>
              <button
                onClick={() => buy(c.id)}
                disabled={buying === c.id}
                data-testid={`buy-credits-${c.id}`}
                className="w-full py-2.5 rounded-sm font-semibold border border-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
              >
                {buying === c.id ? "Redirecting…" : "Buy"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="text-xs text-ink-muted mono">
        Test mode — use Stripe test card 4242 4242 4242 4242, any future date, any CVC.
      </div>
    </AppShell>
  );
}
